<template>
  <div data-testid="preferences-form" class="configuration-form">
    <VerticalWizardContent :title="title">
      <label class="string-input">
        <div class="label">
          <Localization message="create-plan-planned-leave-target" />
        </div>
        <Decimal
          v-model="plannedLeaveTarget"
          :message="validationMessagePlannedLeaveTarget"
          :state="getFieldState($v.plannedLeaveTarget)"
          @input="input"
          append="%"
          :min="0"
          :max="100"
          :step="0.01"
        />
      </label>
      <label class="string-input">
        <div class="label">
          <Localization message="create-plan-unplanned-leave-target" />
        </div>
        <Decimal
          v-model="unplannedLeaveTarget"
          :message="validationMessageUnplannedLeaveTarget"
          :state="getFieldState($v.unplannedLeaveTarget)"
          @input="input"
          append="%"
          :min="0"
          :max="100"
          :step="0.01"
        />
      </label>
      <label class="string-input full-width">
        <div class="label">
          <Localization message="create-plan-shrinkage-target" />
        </div>
        <Decimal
          v-model="shrinkageTarget"
          :message="validationMessageShrinkageTarget"
          :state="getFieldState($v.shrinkageTarget)"
          @input="input"
          append="%"
          :min="0"
          :max="100"
          :step="0.01"
        />
      </label>
      <label class="string-input full-width">
        <div class="label">
          <Localization message="create-plan-attrition-target" />
        </div>
        <Decimal
          v-model="attritionTarget"
          :message="validationMessageAttritionTarget"
          :state="getFieldState($v.attritionTarget)"
          @input="input"
          append="%"
          :min="0"
          :max="100"
          :step="0.01"
        />
      </label>
      <label
        class="string-input"
        :class="{ 'input-error': $v.unitOfWork.$error }"
      >
        <div class="label">
          <Localization message="create-plan-unit-of-work" />
        </div>
        <AlSelect v-model="unitOfWork" :options="unitsOfWork" @input="input">
        </AlSelect>
        <div class="message">
          <Localization
            v-if="!$v.unitOfWork.$error"
            message="create-plan-unit-of-work-explanation"
          />
          <div v-if="$v.unitOfWork.$error">
            {{ validationMessageUnitOfWork }}
          </div>
        </div>
      </label>
      <div class="hours" v-if="isByFTE">
        <label class="string-input">
          <div class="label">
            <Localization message="create-plan-paid-hours-day" />
          </div>
          <Decimal
            v-model="paidDaily"
            :message="validationMessagePaidDaily"
            :state="getFieldState($v.paidDaily)"
            @input="input"
            append="h/d"
            :min="1"
            :max="99"
            :step="0.01"
        /></label>
      </div>
      <div class="hours" v-if="isByFTE">
        <label class="string-input">
          <div class="label">
            <Localization message="create-plan-paid-hours-month" />
          </div>
          <Decimal
            v-model="paidMonthly"
            :message="validationMessagePaidMonthly"
            :state="getFieldState($v.paidMonthly)"
            @input="input"
            append="h/m"
            :min="1"
            :max="999"
            :step="0.01"
        /></label>
      </div>

      <label
        class="string-input configuration-integer-input full-width nesting-time-wrapper"
        v-featureflow:isOn="'capacity-planner-nest-time'"
      >
        <div class="label">
          <Localization message="create-plan-nest-time" />
        </div>
        <Decimal
          v-model="nestTime"
          :message="validationMessageNestTime"
          :state="getFieldStateBoolean($v.nestTime)"
          @input="input"
          append="days"
          :min="0"
          :max="100"
          :step="1"
        />
      </label>
    </VerticalWizardContent>
    <AlButton
      v-if="showSubmitButton"
      type="btn"
      :text="submitButtonText"
      @click="save"
    />
  </div>
</template>

<script>
import { AlButton, AlSelect } from "@playvox/almendra-components";
import {
  VerticalWizardContent,
  Translator,
  Localization,
  Decimal,
} from "@agyletime/wfm-vue-common";

export default {
  name: "ConfigurationForm",
  components: {
    VerticalWizardContent,
    Decimal,
    AlButton,
    Localization,
    AlSelect,
  },
  mixins: [Translator],
  props: {
    value: {
      type: Object,
      required: true,
    },
    validationsSchema: {
      type: Object,
      required: true,
    },
    submitButtonText: {
      type: String,
      default: "Submit",
    },
    showSubmitButton: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    value: {
      handler(newValue) {
        const newValues = this.handleIncomingProps(newValue, this.unitsOfWork);

        Object.keys(newValues).forEach((key) => {
          this[key] = newValues[key];
        });
      },
      deep: true,
    },
  },
  data() {
    const conf = this.value;
    const unitsOfWork = this.getUnitsOfWork();
    return this.handleIncomingProps(conf, unitsOfWork, true);
  },
  computed: {
    isByFTE() {
      if (!this.$v.unitOfWork.$model) {
        return false;
      }
      return this.$v.unitOfWork.$model.id === "FTE";
    },
    validationMessagePlannedLeaveTarget() {
      const validation = this.getValidationMessage(
        this.plannedLeaveTarget,
        this.$v.plannedLeaveTarget,
        "create-plan-planned-leave-target"
      );

      return validation === ""
        ? this.translate("create-plan-planned-leave-target-explanation")
        : validation;
    },
    validationMessageUnplannedLeaveTarget() {
      const validation = this.getValidationMessage(
        this.unplannedLeaveTarget,
        this.$v.unplannedLeaveTarget,
        "create-plan-unplanned-leave-target"
      );

      return validation === ""
        ? this.translate("create-plan-unplanned-leave-target-explanation")
        : validation;
    },
    validationMessageShrinkageTarget() {
      const validation = this.getValidationMessage(
        this.shrinkageTarget,
        this.$v.shrinkageTarget,
        "create-plan-shrinkage-target"
      );

      return validation === ""
        ? this.translate("create-plan-shrinkage-target-explanation")
        : validation;
    },
    validationMessageAttritionTarget() {
      const validation = this.getValidationMessage(
        this.attritionTarget,
        this.$v.attritionTarget,
        "create-plan-attrition-target"
      );

      return validation === ""
        ? this.translate("create-plan-attrition-target-explanation")
        : validation;
    },
    validationMessageNestTime() {
      const validation = this.getValidationMessage(
        this.nestTime,
        this.$v.nestTime,
        "create-plan-nest-time"
      );

      return validation === ""
        ? this.translate("create-plan-nest-time-explanation")
        : validation;
    },
    validationMessageUnitOfWork() {
      if (!this.$v.unitOfWork.$error) {
        return "";
      }
      if (!this.$v.unitOfWork.required) {
        return this.translate("validation-mandatory", {
          field: this.translate("create-plan-unit-of-work"),
        });
      }
      return "";
    },
    validationMessagePaidDaily() {
      let message = this.getValidationMessage(
        this.paidDaily,
        this.$v.paidDaily,
        "create-plan-paid-hours-day",
        "1",
        "99"
      );
      if (!message) {
        message = this.translate("paid-hours-day-explanation");
      }
      return message;
    },
    validationMessagePaidMonthly() {
      // check for paid month greater than day
      if (!this.$v.paidMonthly.paidMonthValidation) {
        return this.translate("paid-monthly-greater-than-daily");
      }
      return this.getValidationMessage(
        this.paidMonthly,
        this.$v.paidMonthly,
        "create-plan-paid-hours-month",
        "1",
        "999"
      );
    },
  },

  methods: {
    getValidationMessage(field, validationObj, messasge, min, max) {
      // check for required
      if (!validationObj.required) {
        return this.translate("validation-mandatory", {
          field: this.translate(messasge),
        });
        // check for negative number
      } else if (!validationObj.validateNegativeNumber) {
        return this.translate("validation-between-number", {
          field: this.translate(messasge),
          min: min ? min : "0",
          max: max ? max : "100",
        });
        // check for negative error
      } else if (!validationObj.$error) {
        return "";
        // check for min and max limit
      } else if (!validationObj.between) {
        return this.translate("validation-between-number", {
          field: this.translate(messasge),
          min: min ? min : "0",
          max: max ? max : "100",
        });
      } else {
        return "";
      }
    },
    onLanguageChange() {
      this.title = this.translate("create-plan-preferences");
    },
    getFieldState(field) {
      if (field.$error) {
        return "error";
      }
      return "";
    },
    getFieldStateBoolean(field) {
      if (field.$error) {
        return false;
      }
      return null;
    },
    handleIncomingProps(conf, unitsOfWork, init = false) {
      const selectedUnitOfWork =
        unitsOfWork.find((unitOfWork) => unitOfWork.id === conf.unitOfWork) ||
        unitsOfWork[0];
      return {
        plannedLeaveTarget: this.getNumberValue(conf.plannedLeaveTarget, init),
        unplannedLeaveTarget: this.getNumberValue(
          conf.unplannedLeaveTarget,
          init
        ),
        shrinkageTarget: this.getNumberValue(conf.shrinkageTarget, init),
        attritionTarget: this.getNumberValue(conf.attritionTarget, init),
        nestTime: this.getNumberValue(conf.nestTime, init),
        unitOfWork: selectedUnitOfWork,
        paidDaily: this.getNumberValue(conf.paidDaily, init),
        paidMonthly: this.getNumberValue(conf.paidMonthly, init),

        unitsOfWork,
      };
    },
    getNumberValue(value, isInit) {
      if (isInit) {
        return value || 0;
      }
      if (value === "") {
        return undefined;
      }
      const num = Number(value);
      if (Number.isNaN(num)) {
        return undefined;
      }
      return num;
    },
    getOption(item) {
      return (item && item.id) || null;
    },
    getUnitsOfWork() {
      return [
        {
          id: "FTE",
          name: "FTE",
        },
        {
          id: "Hours",
          name: "Hours",
        },
      ];
    },
    input() {
      this.$emit("input", {
        plannedLeaveTarget: this.plannedLeaveTarget,
        unplannedLeaveTarget: this.unplannedLeaveTarget,
        shrinkageTarget: this.shrinkageTarget,
        attritionTarget: this.attritionTarget,
        nestTime: this.nestTime,
        unitOfWork: this.getOption(this.unitOfWork),
        paidDaily: this.paidDaily,
        paidMonthly: this.paidMonthly,
      });
    },
    save() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.$emit("next", {
        plannedLeaveTarget: this.plannedLeaveTarget,
        unplannedLeaveTarget: this.unplannedLeaveTarget,
        shrinkageTarget: this.shrinkageTarget,
        attritionTarget: this.attritionTarget,
        nestTime: this.nestTime,
        unitOfWork: this.getOption(this.unitOfWork),
        paidDaily: this.paidDaily,
        paidMonthly: this.paidMonthly,
      });
    },
  },
  validations() {
    return this.validationsSchema;
  },
};
</script>

<style lang="scss" scoped>
.hours {
  display: flex;
  width: auto;
  margin-right: 0;

  & > * {
    margin-right: 2em;
  }
}
</style>

<style>
.configuration-integer-input .dr-input__container {
  width: 8em;
}

.nesting-time-wrapper input {
  text-align: center;
  padding: 12px 5px 9px 12px;
}

.nesting-time-wrapper .append {
  margin-right: 1.8em;
}
</style>
